import { assembleFiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-registry/assemble";
import type { FilterCategories } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import type { SurfaceSelection } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import type { FiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import { filtersConfigStatusMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import { normalizeCategories } from "app/pages/sales-intelligence/modules/advanced-search-filtering/utils/filterConfig";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import createSignalsFilter from "../models/SignalsFilter/factory";
import { leadsFilterRegistry, orderOfLeadsDefinition } from "../registry";
import type { LeadsCapability } from "../types/leadsCapability";
import {
    type LeadsDataSource,
    type LeadsFilterBuildContext,
    leadsDataSourceMap,
} from "../types/leadsFilterBuildContext";
import { type LeadsFilterCategory, leadsFilterCategoryMap } from "../types/leadsFilterCategory";
import type { OrderedFilterInstance } from "../types/leadsSearchFilterInstance";
import { withOrder } from "../utils/withOrder";

import { orderOfGroupMember } from "./filterOrder";

/**
 * The leads filter list for a surface: the registry fold plus the hand-written signals remainder (signal
 * keys are predicate-routed, so signals cannot be a single-key definition). Every filter self-gates — via
 * `requires` (data) and the selection (category / usage / capability) — so there is no hand-curated phase
 * branch and no whole-panel readiness short-circuit: a slow or absent data source hides only its own card.
 * The one exception is the fold's `seedsDefault` gate: while a selected default-carrying filter (e.g.
 * suppression) cannot build yet, the whole surface stays `pending` with an empty list, so the one-shot
 * defaults seed (`useApplyInitialFilters`) cannot run without it.
 */
export const createLeadsSearchFiltersConfig = (
    ctx: LeadsFilterBuildContext,
    ready: ReadonlySet<LeadsDataSource>,
    selection: SurfaceSelection<LeadsFilterCategory, LeadsCapability>,
    loading?: ReadonlySet<LeadsDataSource>,
): FiltersConfig<OrderedFilterInstance[]> => {
    const desiredCategories = selection.categories;

    const isDesiredCategory = (category: FilterCategories): boolean =>
        !desiredCategories ||
        normalizeCategories(category).some((c) => desiredCategories.has(c as LeadsFilterCategory));

    const registryConfig = assembleFiltersConfig({
        registry: leadsFilterRegistry,
        selection,
        ctx,
        ready,
        loading,
        orderOf: orderOfLeadsDefinition,
    });

    if (registryConfig.status === filtersConfigStatusMap.pending) {
        return { filtersList: [], status: filtersConfigStatusMap.pending };
    }

    const registryFilters = registryConfig.filtersList as readonly OrderedFilterInstance[];

    // The panel is "ready" once its core data has loaded (description, plus technologies/signals when those
    // categories are in scope). Suppression and the industry taxonomy are deliberately NOT part of this gate:
    // each self-gates via `requires`, so a slow or absent one hides its own card rather than the whole panel.
    const isReady =
        ready.has(leadsDataSourceMap.description) &&
        (!isDesiredCategory(leadsFilterCategoryMap.website) || ready.has(leadsDataSourceMap.technologies)) &&
        (!isDesiredCategory(leadsFilterCategoryMap.signals) || ready.has(leadsDataSourceMap.signals));

    const signalsFilters: OrderedFilterInstance[] = isReady
        ? ctx.signalsListViewModel.entries.map((entry, index) =>
              withOrder(createSignalsFilter(entry), orderOfGroupMember("signals", index)),
          )
        : [];

    const filters: (OrderedFilterInstance | undefined)[] = [...signalsFilters, ...registryFilters];

    return {
        status: isReady ? filtersConfigStatusMap.ready : filtersConfigStatusMap.initial,
        filtersList: filters.filter(
            (c): c is OrderedFilterInstance => !is.undefined(c) && isDesiredCategory(c.category),
        ),
    };
};
